import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';

const scenes = [
  {
    name: 'Ceremony',
    src: 'https://ik.imagekit.io/aeu02xvjgn/Updated%20images/ChatGPT%20Image%20May%202,%202026,%2007_48_01%20PM.png',
    alt: 'Outdoor wedding ceremony at Padua Hills Theatre in Claremont',
    position: 'center 48%',
    mobilePosition: 'center 42%',
  },
  {
    name: 'Reception',
    src: 'https://ik.imagekit.io/aeu02xvjgn/Updated%20images/ChatGPT%20Image%20May%202,%202026,%2007_32_58%20PM.png',
    alt: 'Wedding reception inside the historic Padua Hills Theatre ballroom',
    position: 'center 48%',
    mobilePosition: 'center 40%',
  },
  {
    name: 'Estate',
    src: 'https://ik.imagekit.io/aeu02xvjgn/Updated%20images/ChatGPT%20Image%20May%202,%202026,%2007_30_31%20PM.png',
    alt: 'Private estate wedding venue exterior at Padua Hills Theatre in Claremont',
    position: 'center 50%',
    mobilePosition: 'center 43%',
  },
];

function useReducedMotion() {
  const [reducedMotion, setReducedMotion] = useState(() =>
    typeof window !== 'undefined' && window.matchMedia('(prefers-reduced-motion: reduce)').matches
  );

  useEffect(() => {
    const mediaQuery = window.matchMedia('(prefers-reduced-motion: reduce)');
    const updatePreference = () => setReducedMotion(mediaQuery.matches);

    updatePreference();
    mediaQuery.addEventListener('change', updatePreference);
    return () => mediaQuery.removeEventListener('change', updatePreference);
  }, []);

  return reducedMotion;
}

function CornerMarks() {
  return (
    <div className="pointer-events-none absolute inset-5 z-10 sm:inset-8" aria-hidden="true">
      <span className="absolute left-0 top-0 h-7 w-7 border-l border-t border-brand-vintage/35" />
      <span className="absolute right-0 top-0 h-7 w-7 border-r border-t border-brand-vintage/35" />
      <span className="absolute bottom-0 left-0 h-7 w-7 border-b border-l border-brand-vintage/35" />
      <span className="absolute bottom-0 right-0 h-7 w-7 border-b border-r border-brand-vintage/35" />
    </div>
  );
}

export default function Hero() {
  const [activeScene, setActiveScene] = useState(0);
  const [transitionFrom, setTransitionFrom] = useState(null);
  const reducedMotion = useReducedMotion();

  useEffect(() => {
    if (reducedMotion) return undefined;

    const timer = window.setTimeout(() => {
      setTransitionFrom(activeScene);
      setActiveScene((activeScene + 1) % scenes.length);
    }, 4000);

    return () => window.clearTimeout(timer);
  }, [activeScene, reducedMotion]);

  const finishTransition = (event) => {
    if (event.animationName === 'paduaSceneEnter') setTransitionFrom(null);
  };

  return (
    <section
      id="home"
      className="hero-stage relative isolate flex min-h-[100svh] items-center overflow-hidden bg-brand-charcoal text-brand-vintage"
    >
      <style>{`
        @keyframes paduaSceneEnter {
          from { opacity: 0; }
          to { opacity: 1; }
        }
        @keyframes paduaSceneLeave {
          from { opacity: 1; }
          to { opacity: 0; }
        }
        @keyframes paduaSceneZoom {
          from { transform: scale(1); }
          to { transform: scale(1.045); }
        }
        .hero-scene-entering { animation: paduaSceneEnter 1.2s ease-in-out both; }
        .hero-scene-leaving { animation: paduaSceneLeave 1.2s ease-in-out both; }
        .hero-photo-zoom { animation: paduaSceneZoom 4s ease-out both; }
        .hero-scene-image { object-position: var(--hero-position); }
        .hero-link:focus-visible {
          outline: 2px solid #FCFAF6;
          outline-offset: 4px;
        }
        @media (max-width: 639px) {
          .hero-scene-image {
            object-position: var(--hero-position-mobile, var(--hero-position));
          }
        }
        @media (prefers-reduced-motion: reduce) {
          .hero-stage *,
          .hero-stage *::before,
          .hero-stage *::after {
            animation-duration: 0.01ms !important;
            animation-iteration-count: 1 !important;
            scroll-behavior: auto !important;
            transition-duration: 0.01ms !important;
          }
        }
      `}</style>

      <div className="absolute inset-0" aria-hidden="true">
        {scenes.map((scene, index) => {
          const isActive = activeScene === index;
          const isEntering = transitionFrom !== null && isActive;
          const isLeaving = transitionFrom === index;
          const isVisibleLayer = isActive || isLeaving;

          return (
            <div
              key={scene.src}
              onAnimationEnd={finishTransition}
              className={`absolute inset-0 ${
                isEntering ? 'hero-scene-entering' : isLeaving ? 'hero-scene-leaving' : ''
              }`}
              style={{
                opacity: isVisibleLayer ? 1 : 0,
              }}
            >
              <img
                src={scene.src}
                alt={scene.alt}
                aria-hidden={!isVisibleLayer}
                loading={index === 0 ? 'eager' : 'lazy'}
                fetchPriority={index === 0 ? 'high' : 'auto'}
                decoding="async"
                className={`hero-scene-image h-full w-full object-cover ${
                  isVisibleLayer && !reducedMotion ? 'hero-photo-zoom' : ''
                }`}
                style={{
                  '--hero-position': scene.position,
                  '--hero-position-mobile': scene.mobilePosition,
                  }}
              />
            </div>
          );
        })}
        <div className="absolute inset-0 bg-gradient-to-r from-brand-charcoal/95 via-brand-charcoal/75 to-brand-charcoal/15" />
        <div className="absolute inset-0 bg-gradient-to-t from-brand-charcoal/85 via-transparent to-brand-charcoal/35" />
        <div className="absolute inset-0 bg-brand-charcoal/10" />
      </div>

      <CornerMarks />

      <div className="relative z-20 w-full px-6 pb-40 pt-28 sm:px-10 sm:pt-32 lg:px-16 lg:pb-40">
        <div className="max-w-7xl">
          <div className="max-w-2xl text-left">
            <p className="mb-5 font-sans text-[10px] font-semibold uppercase tracking-[0.34em] text-brand-stone sm:text-xs">
              Claremont · California
            </p>
            <h1 className="mb-6 font-serif text-[2.1rem] font-light leading-[1.08] tracking-tight text-brand-vintage sm:text-5xl lg:text-[4.5rem]">
              A Historic Wedding Venue
              <br />
              <span className="italic text-brand-stone">in Claremont, California</span>
            </h1>
            <p className="mb-9 max-w-lg font-sans text-sm font-light leading-relaxed tracking-wide text-brand-vintage/85 sm:text-base lg:text-lg">
              Spanish Revival architecture, olive grove ceremonies, and evenings in the historic ballroom.
            </p>
            <div className="flex w-full flex-col gap-3 sm:w-auto sm:flex-row sm:flex-wrap sm:gap-4">
              <Link
                to="/weddings/private-tour"
                className="hero-link inline-flex min-h-11 w-full items-center justify-center border border-brand-olive bg-brand-olive px-7 py-3 text-center font-sans text-[10px] font-semibold uppercase tracking-[0.2em] text-brand-vintage transition-colors hover:border-brand-vintage hover:bg-brand-vintage hover:text-brand-charcoal sm:w-auto sm:px-8"
              >
                Plan a Private Tour
              </Link>
              <Link
                to="/weddings/gallery"
                className="hero-link inline-flex min-h-11 w-full items-center justify-center border border-brand-vintage/65 bg-brand-charcoal/10 px-7 py-3 text-center font-sans text-[10px] font-semibold uppercase tracking-[0.2em] text-brand-vintage backdrop-blur-sm transition-colors hover:bg-brand-vintage/15 sm:w-auto sm:px-8"
              >
                Explore the Estate
              </Link>
            </div>
          </div>

        </div>
      </div>
    </section>
  );
}